import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import { EventType } from '@/features/events/shared/types'
import { ToolCodeBlock, ToolHeader } from '../shared'
import { formatBashCommand } from '../utils/bashCommandFormatter'
import { LazyCollapsibleContent } from './shared'

interface BashContextRendererProps {
  readonly command: string
  readonly description?: string | undefined
  readonly enableCollapse?: boolean | undefined
}

export const BashContextRenderer: FC<BashContextRendererProps> = ({
  command,
  description,
  enableCollapse = true,
}) => {
  const theme = useTheme()
  const formattedCommand = useMemo(() => formatBashCommand(command), [command])

  return (
    <>
      <ToolHeader eventType={EventType.TOOL_PERMISSION_REQUEST} toolName="Bash" />

      {description && (
        <Box sx={descriptionContainerSx}>
          <Typography variant="body2" sx={descriptionTextSx(theme)}>
            {description}
          </Typography>
        </Box>
      )}

      <Box sx={contentContainerSx}>
        <LazyCollapsibleContent content={formattedCommand} enableCollapse={enableCollapse}>
          {(displayContent) => (
            <ToolCodeBlock
              code={displayContent}
              language="bash"
              showLineNumbers={false}
              hideContinuationBackslashes={true}
            />
          )}
        </LazyCollapsibleContent>
      </Box>
    </>
  )
}

const descriptionContainerSx: SxProps<Theme> = {
  mt: 1,
  mb: 1.5,
}

const contentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

const descriptionTextSx = (theme: Theme): SxProps<Theme> => ({
  fontStyle: 'italic',
  color: theme.palette.text.secondary,
  fontSize: 'inherit',
})
